/**
 * Issue template module.
 *
 * Contains the Copilot prompt template and catalog-info schema embedded as
 * string constants so the frontend can build the full issue body without
 * needing a backend file-read.
 */

// ---------------------------------------------------------------------------
// Catalog-info JSON-Schema (YAML) – embedded verbatim
// ---------------------------------------------------------------------------
export const CATALOG_INFO_SCHEMA = `%YAML 1.1
---
$schema: "http://json-schema.org/draft-04/schema"
id: "http://bayer.com/schemas/backstage/catalog-info-1.0.0"
title: "Backstage Catalog Info"
description: "Schema for Backstage catalog-info.yaml files"
examples:
  - |
      apiVersion: backstage.io/v1alpha1
      kind: Component
      metadata:
        title: Cloud Docs
        name: cloud-docs
        description: Go/Cloud markdown repository
        annotations:
          github.com/project-slug: bayer-int/test-backstage/tree/master/api/club
type: object
properties:
  apiVersion:
    type: string
    description: "The API version of the entity format."
    enum:
      - backstage.io/v1alpha1
  kind:
    type: string
    description: "The kind of the entity."
    enum:
      - Component
      - API
      - Location
      - User
      - Group
      - System
      - Domain
      - Resource
      - Template
  metadata:
    type: object
    description: "Metadata about the entity."
    properties:
      name:
        type: string
        description: "The name of the entity."
        pattern: '\\A[a-zA-Z0-9][a-zA-Z0-9_.-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]\\z'
      description:
        type: string
        description: "A brief description of the entity."
      annotations:
        type: object
        description: "A set of key-value pairs for annotations."
        additionalProperties:
          type: string
      tags:
        type: array
        description: "A list of tags associated with the entity."
        items:
          type: string
      labels:
        type: object
        description: "A set of key-value pairs for labels."
        properties:
          division:
            type: string
            description: "The division label for the entity."
          platform:
            type: string
            description: "The platform label for the entity."
      links:
        type: array
        description: "A list of links related to the entity."
        items:
          type: object
          properties:
            url:
              type: string
              description: "The URL of the link."
            title:
              type: string
              description: "A human-readable label for the link."
            icon:
              type: string
              description: "An icon identifier for the link (e.g. home, dashboard, docs)."
          required:
            - url
    required:
      - name
  spec:
    type: object
    description: "Specification details of the entity."
    properties:
      type:
        type: string
        description: "The type of the entity."
        enum:
          - service
          - website
          - library
          - documentation
          - tool
          - data-pipeline
          - machine-learning
          - other
          - openapi
          - asyncapi
          - graphql
          - grpc
          - url
          - file
          - mcp
          - agent
          - llm
          - custom
      lifecycle:
        type: string
        description: "The lifecycle stage of the entity."
        enum:
          - experimental
          - production
          - deprecated
          - unknown
      owner:
        type: string
        description: "The owner of the entity."
      system:
        type: string
        description: "The system the entity belongs to."
      domain:
        type: string
        description: "The domain the entity is part of."
      subcomponentOf:
        type: string
      providesApi:
        type: array
        description: "List of APIs provided by the entity."
        items:
          type: string
      consumesApi:
        type: array
        description: "List of APIs consumed by the entity."
        items:
          type: string
      dependsOn:
        type: array
        description: "List of dependencies of the entity."
        items:
          type: string
      target:
        type: string
        description: "The target of the entity."
      targets:
        type: array
        description: "List of targets for the entity."
        items:
          type: string
      presence:
        type: string
        description: "The presence status of the entity."
        enum:
          - required
          - optional
      transportType:
        type: string
        description: "The transport type of the entity."
        enum:
          - http
          - sse
          - stdio
      mcpUrl:
        type: string
        description: "The MCP URL for the entity."
      a2aCard:
        type: object
        description: "The A2A card associated with the entity."
      definition:
        type: object
        properties:
          oneOf:
            $text:
              type: string
              description: "Path or URL to the API definition."
            $openapi:
              type: string
              description: "Path or URL to the OpenAPI specification."
    required:
      - type
required:
  - apiVersion
  - kind
  - metadata
  - spec
anyOf:
  - properties:
      kind:
        enum:
          - Component
          - API
      spec:
        required:
          - owner
  - properties:
      kind:
        not:
          enum:
            - Component
            - API
additionalProperties: false`;

// ---------------------------------------------------------------------------
// Prompt template – embedded verbatim from issue-prompt.md
// ---------------------------------------------------------------------------
const PROMPT_TEMPLATE = `# Backstage Catalog Registration

## Objective
Generate a well-structured \`catalog-info.yaml\` file to register this repository in Backstage. If this is an API project, ensure proper API documentation exists or create it.

## Scope (strict)
This is a metadata-only change. The Pull Request may only add or update:
- \`catalog-info.yaml\` files
- An API specification file (e.g. \`openapi.yaml\`, \`schema.graphql\`) when the API has none
- A single changelog entry, **only** when the repository requires one (see **Changelog entries**)

Do **not**:
- Modify application source code, tests, configuration, dependencies, lockfiles, or CI workflows
- Fix bugs, refactor, or change API behavior, even if the code looks inconsistent, ambiguous, or unsafe
- Add READMEs or any other files, even if repository conventions or instructions ask for them

### Changelog entries
Add a changelog entry only if the repository requires one for every change, for example a CI check, a PR template or contributing guide rule, a \`.github/copilot-instructions.md\` rule, or a tool such as Changesets. If there is no such requirement, do not add one.

When one is required:
- Add exactly one entry in the repository's existing format and location
- Describe only the Backstage registration (e.g. "Add Backstage catalog metadata" or "Add Backstage catalog metadata and OpenAPI definition")
- Do not change package versions, edit existing entries, or add release metadata beyond the entry itself
- If the repository uses Changesets, use a \`patch\` bump unless the repository documents a different bump for documentation-only changes

Document the API **as it is currently implemented**. If behavior is ambiguous or looks unsafe, describe it as-is in the spec and list the concern under a "Follow-ups for maintainers" section in the Pull Request description instead of changing code.

## Requirements

### 1. Repository Analysis
- Scan the repository to determine the project type: **API** or **Component**
- Identify the technology stack, frameworks, and primary purpose
- **For monorepos**: Detect and handle multiple components/APIs (create separate \`catalog-info.yaml\` files in each subdirectory as needed)

### 2. API Documentation (APIs only)
- **Check for existing API specifications**:
  - OpenAPI/Swagger (\`.yaml\`, \`.json\`)
  - GraphQL schema (\`.graphql\`, \`schema.graphql\`)
  - API Blueprint, RAML, or other formats

- **If documentation exists**:
  - Reference it in the \`spec.definition\` field of \`catalog-info.yaml\`

- **If no documentation exists**:
  - **REST APIs**: Generate an \`openapi.yaml\` file by analyzing:
    - Route definitions
    - Request/response handlers
    - Data models and schemas
    - Authentication mechanisms
  - **GraphQL APIs**: Generate a \`schema.graphql\` file from the schema definition
  - Place the generated file in the appropriate directory (root for single repos, component directory for monorepos)
  - Reference the new file in \`catalog-info.yaml\`

### 3. Catalog File Generation
Create \`catalog-info.yaml\` following the schema:
\`\`\`yaml
{{catalog-info-schema}}
\`\`\`

**Required fields**:
- \`apiVersion\`: \`backstage.io/v1alpha1\`
- \`kind\`: \`API\` or \`Component\`
- \`metadata.name\`: Kebab-case identifier (e.g., \`my-api-service\`)
- \`metadata.title\`: Human-readable name
- \`metadata.description\`: Clear, concise description of purpose
- \`spec.owner\`: Owning team (see **Owner** below). Backstage rejects \`Component\` and \`API\` entities without an owner, so never omit it.

**Additional considerations**:
- \`metadata.annotations.github.com/project-slug\`: Repository path
- \`metadata.tags\`: Relevant tags for discoverability (e.g., \`python\`, \`rest-api\`, \`graphql\`)
- \`spec.type\`: For APIs, specify type (\`openapi\`, \`graphql\`, \`grpc\`, etc.)
- \`spec.lifecycle\`: \`production\`, \`experimental\`, \`deprecated\`

### Owner
Set \`spec.owner\` on every \`Component\` and \`API\` entity, using the format \`group:default/<github-team-slug>\`.

Suggested owners (GitHub teams with admin or maintain access to this repository, most privileged first): {{suggestedOwners}}

Choose the owner in this order:
1. A team listed in \`CODEOWNERS\` (\`.github/CODEOWNERS\`, \`CODEOWNERS\`, or \`docs/CODEOWNERS\`), when it covers the root of the repository
2. The first suggested owner above
3. If neither is available, \`user:default/<issue author GitHub login in lowercase>\`, and add "Confirm \`spec.owner\`" under "Follow-ups for maintainers" in the Pull Request description

**Add following "metadata.labels" if they are available below (value is different from "N/A"):**
- beatId: {{beatId}}
- division: {{division}}
- platform: {{platform}}
- businessPortfolio: {{businessPortfolio}}

## Implementation Steps

1. **Analyze repository structure**
   - Identify all components/services (especially important for monorepos)
   - Determine project types and their dependencies

2. **For each component/API found**:
   - Check for existing API documentation
   - Generate documentation if missing
   - Create or update \`catalog-info.yaml\` in the appropriate location

3. **Generate Pull Request**
   - Include all \`catalog-info.yaml\` files
   - Include any generated API documentation files
   - Include nothing else (see **Scope**)
   - Provide clear commit messages explaining what was added

## Example Structure

### For API (with OpenAPI):
\`\`\`yaml
apiVersion: backstage.io/v1alpha1
kind: API
metadata:
  name: user-management-api
  title: User Management API
  description: RESTful API for user CRUD operations
  annotations:
    github.com/project-slug: bayer-staging/apiCatalogGeneration-innovationSprint
  tags:
    - rest
    - python
    - flask
spec:
  type: openapi
  lifecycle: production
  owner: group:default/platform-team
  definition:
    $text: ./openapi.yaml
\`\`\`

### For Component:
\`\`\`yaml
apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: data-processor
  title: Data Processing Service
  description: Background service for data transformation
  annotations:
    github.com/project-slug: bayer-staging/apiCatalogGeneration-innovationSprint
  tags:
    - python
    - batch-processing
spec:
  type: service
  lifecycle: production
  owner: group:default/data-team
\`\`\`

## Monorepo Handling (Bonus)
If the repository contains multiple services/components:
- Create a \`catalog-info.yaml\` in each service/component directory
- Ensure each has a unique \`metadata.name\`
- Consider creating a root-level catalog file that references child components if appropriate
- Create a top-level \`catalog-info.yaml\` of kind \`Location\` that references all sub-catalog files

**Example root-level catalog for monorepo:**
\`\`\`yaml
apiVersion: backstage.io/v1alpha1
kind: Location
metadata:
  name: my-monorepo
  description: Parent catalog for all services in this monorepo
spec:
  type: url
  targets:
    - ./service-a/catalog-info.yaml
    - ./service-b/catalog-info.yaml
    - ./service-c/catalog-info.yaml
\`\`\`

## Notes
- BeatId can be found in the workflow event.json under the topics key
- Ensure all file paths in \`spec.definition.$text\` are relative and correct
- Validate YAML syntax before committing
- Before committing, confirm every \`Component\` and \`API\` entity has \`spec.owner\`
- Follow existing repository conventions for where files are placed (this does not permit changes outside **Scope**)`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface BeatApplication {
  beatId: string;
  name: string;
  businessOwnerCwid: string | null;
  systemOwnerCwid: string | null;
  systemOwnerDelegateCwid: string | null;
  leadBusinessPortfolio: string | null;
}

export interface PlatformResult {
  division: string;
  platform: string;
}

/**
 * Builds the full GitHub Issue body by substituting template placeholders.
 */
export function prepareIssueBody(
  beatApplication: BeatApplication | null,
  division: string | null,
  platform: string | null,
  suggestedOwners: string[] = [],
): string {
  const suggestedOwnersText =
    suggestedOwners.length > 0
      ? suggestedOwners.map(owner => `\`${owner}\``).join(', ')
      : 'N/A';

  return PROMPT_TEMPLATE.replace(
    '{{beatId}}',
    beatApplication?.beatId || 'N/A',
  )
    .replace(
      '{{businessPortfolio}}',
      beatApplication?.leadBusinessPortfolio || 'N/A',
    )
    .replace('{{division}}', division || 'N/A')
    .replace('{{platform}}', platform || 'N/A')
    .replace('{{suggestedOwners}}', suggestedOwnersText)
    .replace('{{catalog-info-schema}}', CATALOG_INFO_SCHEMA);
}
